// MaturaTriangleSineCosinePFClosedTask.js
// Zadanie maturalne zamknięte P/F: dwa boki, kąt między nimi, pole i równoramienność.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";

const ANGLES = {
  30: {
    sin: { num: 1, den: 2, root: null, latex: "\\frac{1}{2}" },
    cos: { num: 1, den: 2, root: 3, latex: "\\frac{\\sqrt{3}}{2}" },
  },
  45: {
    sin: { num: 1, den: 2, root: 2, latex: "\\frac{\\sqrt{2}}{2}" },
    cos: { num: 1, den: 2, root: 2, latex: "\\frac{\\sqrt{2}}{2}" },
  },
  60: {
    sin: { num: 1, den: 2, root: 3, latex: "\\frac{\\sqrt{3}}{2}" },
    cos: { num: 1, den: 2, root: null, latex: "\\frac{1}{2}" },
  },
};

const NON_ISOSCELES_CASES = [
  { AB: 11, BC: 12, angleB: 60, isIsosceles: false },
  { AB: 8, BC: 9, angleB: 60, isIsosceles: false },
  { AB: 10, BC: 13, angleB: 60, isIsosceles: false },
  { AB: 6, BC: 10, angleB: 30, isIsosceles: false },
  { AB: 8, BC: 12, angleB: 30, isIsosceles: false },
  { AB: 9, BC: 14, angleB: 30, isIsosceles: false },
  { AB: 7, BC: 10, angleB: 45, isIsosceles: false },
  { AB: 8, BC: 11, angleB: 45, isIsosceles: false },
  { AB: 9, BC: 13, angleB: 45, isIsosceles: false },
];

const HIDDEN_ISOSCELES_CASES = [
  {
    AB: 6,
    ABLatex: "6",
    BC: 6 * Math.SQRT2,
    BCLatex: "6\\sqrt{2}",
    angleB: 45,
    isIsosceles: true,
    correctAreaLatex: "18",
    acSquaredLatex: "36",
    equalSidesLatex: "|AC|=|AB|=6",
    wrongAreas: ["36", "9", "18\\sqrt{2}"],
  },
  {
    AB: 8,
    ABLatex: "8",
    BC: 8 * Math.SQRT2,
    BCLatex: "8\\sqrt{2}",
    angleB: 45,
    isIsosceles: true,
    correctAreaLatex: "32",
    acSquaredLatex: "64",
    equalSidesLatex: "|AC|=|AB|=8",
    wrongAreas: ["64", "16", "32\\sqrt{2}"],
  },
  {
    AB: 6 * Math.SQRT2,
    ABLatex: "6\\sqrt{2}",
    BC: 6,
    BCLatex: "6",
    angleB: 45,
    isIsosceles: true,
    correctAreaLatex: "18",
    acSquaredLatex: "36",
    equalSidesLatex: "|AC|=|BC|=6",
    wrongAreas: ["36", "9", "18\\sqrt{2}"],
  },
  {
    AB: 6,
    ABLatex: "6",
    BC: 6 * Math.sqrt(3),
    BCLatex: "6\\sqrt{3}",
    angleB: 30,
    isIsosceles: true,
    correctAreaLatex: "9\\sqrt{3}",
    acSquaredLatex: "36",
    equalSidesLatex: "|AC|=|AB|=6",
    wrongAreas: ["18\\sqrt{3}", "\\frac{9\\sqrt{3}}{2}", "27"],
  },
  {
    AB: 8,
    ABLatex: "8",
    BC: 8 * Math.sqrt(3),
    BCLatex: "8\\sqrt{3}",
    angleB: 30,
    isIsosceles: true,
    correctAreaLatex: "16\\sqrt{3}",
    acSquaredLatex: "64",
    equalSidesLatex: "|AC|=|AB|=8",
    wrongAreas: ["32\\sqrt{3}", "8\\sqrt{3}", "48"],
  },
];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = (arr) => arr[randInt(0, arr.length - 1)];

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function formatCoeffWithRoot(num, den = 1, root = null) {
  const d = gcd(num, den);
  const n = num / d;
  const q = den / d;

  if (!root) {
    return q === 1 ? `${n}` : `\\frac{${n}}{${q}}`;
  }

  if (q === 1) {
    return n === 1 ? `\\sqrt{${root}}` : `${n}\\sqrt{${root}}`;
  }

  return n === 1
    ? `\\frac{\\sqrt{${root}}}{${q}}`
    : `\\frac{${n}\\sqrt{${root}}}{${q}}`;
}

function areaLatex(AB, BC, angleB, multiplierNum = 1, multiplierDen = 1) {
  const sin = ANGLES[angleB].sin;
  const num = AB * BC * sin.num * multiplierNum;
  const den = 2 * sin.den * multiplierDen;
  return formatCoeffWithRoot(num, den, sin.root);
}

function thirdSideSquaredLatex(AB, BC, angleB) {
  const base = AB * AB + BC * BC;
  const cos = ANGLES[angleB].cos;
  const coeffNum = 2 * AB * BC * cos.num;
  const coeffDen = cos.den;
  const d = gcd(coeffNum, coeffDen);
  const coeff = coeffNum / d;
  const den = coeffDen / d;

  if (!cos.root) {
    const value = base - coeff / den;
    return Number.isInteger(value) ? String(value) : `${base}-\\frac{${coeff}}{${den}}`;
  }

  const radicalPart =
    den === 1
      ? `${coeff}\\sqrt{${cos.root}}`
      : `\\frac{${coeff}\\sqrt{${cos.root}}}{${den}}`;
  return `${base}-${radicalPart}`;
}

function makeWrongAreaLatex(AB, BC, angleB, correctLatex) {
  const candidates = [
    areaLatex(AB, BC, angleB, 2, 1), // brak czynnika 1/2
    areaLatex(AB, BC, angleB, 1, 2), // jeszcze raz podzielone przez 2
    areaLatex(AB, BC, angleB, 3, 2),
  ];

  const sin = ANGLES[angleB].sin;
  if (sin.root) {
    candidates.push(formatCoeffWithRoot(AB * BC, 4, null)); // zgubiony pierwiastek
  } else {
    candidates.push(formatCoeffWithRoot(AB * BC, 4, 2)); // sztucznie dodany pierwiastek
  }

  const unique = candidates.filter((x, idx) => x !== correctLatex && candidates.indexOf(x) === idx);
  return pick(unique.length ? unique : [areaLatex(AB, BC, angleB, 3, 1)]);
}

function sideLatex(value, fallback) {
  if (fallback) return fallback;
  return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(".", "{,}");
}

function LatexLabel({
  x,
  y,
  math,
  color = "currentColor",
  width = 52,
  height = 24,
  anchor = "middle",
  fontSize = 14,
}) {
  const boxX = anchor === "start" ? x : anchor === "end" ? x - width : x - width / 2;
  const textAlign = anchor === "start" ? "left" : anchor === "end" ? "right" : "center";

  return (
    <foreignObject x={boxX} y={y - height / 2} width={width} height={height}>
      <div style={{ color, fontSize, lineHeight: 1.15, textAlign }}>
        <InlineMath math={String(math)} />
      </div>
    </foreignObject>
  );
}

function IncludedAngleTriangleDiagram({ AB, BC, ABLatex, BCLatex, angleB }) {
  const size = 360;
  const rawA = { x: 0, y: 0 };
  const rawB = { x: AB, y: 0 };
  const rad = (angleB * Math.PI) / 180;
  const rawC = {
    x: AB - BC * Math.cos(rad),
    y: -BC * Math.sin(rad),
  };

  const minX = Math.min(rawA.x, rawB.x, rawC.x);
  const maxX = Math.max(rawA.x, rawB.x, rawC.x);
  const minY = Math.min(rawA.y, rawB.y, rawC.y);
  const maxY = Math.max(rawA.y, rawB.y, rawC.y);
  const rawWidth = Math.max(maxX - minX, 1);
  const rawHeight = Math.max(maxY - minY, 1);
  const svgHeight = Math.max(220, Math.min(300, 145 + (rawHeight / rawWidth) * 210));
  const paddingX = 48;
  const paddingY = 42;
  const scale = Math.min(
    (size - paddingX * 2) / rawWidth,
    (svgHeight - paddingY * 2) / rawHeight
  );
  const drawWidth = rawWidth * scale;
  const drawHeight = rawHeight * scale;
  const left = (size - drawWidth) / 2;
  const top = (svgHeight - drawHeight) / 2;

  const map = (p) => ({
    x: left + (p.x - minX) * scale,
    y: top + (p.y - minY) * scale,
  });

  const A = map(rawA);
  const B = map(rawB);
  const C = map(rawC);
  const centroid = { x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 };
  const COLORS = { base: "currentColor", accent: "#f59e0b" };

  const pointLabel = (P, label, offset = 17) => {
    const vx = P.x - centroid.x;
    const vy = P.y - centroid.y;
    const len = Math.hypot(vx, vy) || 1;
    const x = P.x + (vx / len) * offset;
    const y = P.y + (vy / len) * offset;
    const anchor = vx > 8 ? "start" : vx < -8 ? "end" : "middle";
    return { x, y, label, anchor };
  };

  const segmentLabel = (P, Q, label, offset = 15) => {
    const mid = { x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 };
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const len = Math.hypot(dx, dy) || 1;
    const n1 = { x: -dy / len, y: dx / len };
    const n2 = { x: dy / len, y: -dx / len };
    const p1 = { x: mid.x + n1.x * offset, y: mid.y + n1.y * offset };
    const p2 = { x: mid.x + n2.x * offset, y: mid.y + n2.y * offset };
    const d1 = Math.hypot(p1.x - centroid.x, p1.y - centroid.y);
    const d2 = Math.hypot(p2.x - centroid.x, p2.y - centroid.y);
    return d1 >= d2 ? { ...p1, label } : { ...p2, label };
  };

  const arcR = Math.max(24, Math.min(34, Math.min(AB, BC) * scale * 0.17));
  const startAngle = Math.PI;
  const endAngle = Math.PI + rad;
  const arcStart = {
    x: B.x + arcR * Math.cos(startAngle),
    y: B.y + arcR * Math.sin(startAngle),
  };
  const arcEnd = {
    x: B.x + arcR * Math.cos(endAngle),
    y: B.y + arcR * Math.sin(endAngle),
  };
  const labelR = arcR + 17;
  const midAngle = startAngle + rad / 2;
  const angleLabel = {
    x: B.x + labelR * Math.cos(midAngle),
    y: B.y + labelR * Math.sin(midAngle),
  };

  const labels = [pointLabel(A, "A"), pointLabel(B, "B"), pointLabel(C, "C")];
  const abLabel = segmentLabel(A, B, ABLatex, 17);
  const bcLabel = segmentLabel(B, C, BCLatex, 17);

  return (
    <svg width={size} height={svgHeight} viewBox={`0 0 ${size} ${svgHeight}`} style={{ display: "block", width: "100%", maxWidth: size, height: "auto", margin: "0 auto" }}>
      <line x1={A.x} y1={A.y} x2={B.x} y2={B.y} stroke={COLORS.base} strokeWidth="2.35" />
      <line x1={B.x} y1={B.y} x2={C.x} y2={C.y} stroke={COLORS.base} strokeWidth="2.35" />
      <line x1={C.x} y1={C.y} x2={A.x} y2={A.y} stroke={COLORS.base} strokeWidth="2.35" />

      <path
        d={`M ${arcStart.x} ${arcStart.y} A ${arcR} ${arcR} 0 0 1 ${arcEnd.x} ${arcEnd.y}`}
        fill="none"
        stroke={COLORS.accent}
        strokeWidth="2.4"
      />

      <circle cx={A.x} cy={A.y} r="4" fill={COLORS.base} />
      <circle cx={B.x} cy={B.y} r="4" fill={COLORS.base} />
      <circle cx={C.x} cy={C.y} r="4" fill={COLORS.base} />

      <LatexLabel x={abLabel.x} y={abLabel.y} math={abLabel.label} color={COLORS.accent} />
      <LatexLabel x={bcLabel.x} y={bcLabel.y} math={bcLabel.label} color={COLORS.accent} />
      <LatexLabel x={angleLabel.x} y={angleLabel.y} math={`${angleB}^{\\circ}`} color={COLORS.accent} width={58} />

      {labels.map((item) => (
        <LatexLabel key={item.label} x={item.x} y={item.y} math={item.label} anchor={item.anchor} />
      ))}
    </svg>
  );
}

function generateMay2025Original() {
  return {
    type: "MaturaTriangleSineCosinePFClosedTask",
    data: {
      AB: 11,
      ABLatex: "11",
      BC: 12,
      BCLatex: "12",
      angleB: 60,
      isIsosceles: false,
      correctAreaLatex: "33\\sqrt{3}",
      displayedAreaLatex: "33\\sqrt{3}",
      areaTruth: true,
      statements: [
        { id: "isosceles", truth: false },
        { id: "area", truth: true, latex: "33\\sqrt{3}" },
      ],
      acSquaredLatex: "133",
      equalSidesLatex: "",
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 21,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_MAJ"
  ) {
    return generateMay2025Original();
  }

  const baseCase = Math.random() < 0.25 ? pick(HIDDEN_ISOSCELES_CASES) : pick(NON_ISOSCELES_CASES);
  const { AB, BC, angleB, isIsosceles } = baseCase;
  const ABLatex = sideLatex(AB, baseCase.ABLatex);
  const BCLatex = sideLatex(BC, baseCase.BCLatex);
  const correctAreaLatex = baseCase.correctAreaLatex || areaLatex(AB, BC, angleB);
  const areaTruth = Math.random() < 0.5;
  const displayedAreaLatex = areaTruth
    ? correctAreaLatex
    : pick(baseCase.wrongAreas || [makeWrongAreaLatex(AB, BC, angleB, correctAreaLatex)]);

  const statements = [
    { id: "isosceles", truth: isIsosceles },
    { id: "area", truth: areaTruth, latex: displayedAreaLatex },
  ];

  return {
    type: "MaturaTriangleSineCosinePFClosedTask",
    data: {
      AB,
      ABLatex,
      BC,
      BCLatex,
      angleB,
      isIsosceles,
      correctAreaLatex,
      displayedAreaLatex,
      areaTruth,
      statements,
      acSquaredLatex: baseCase.acSquaredLatex || thirdSideSquaredLatex(AB, BC, angleB),
      equalSidesLatex: baseCase.equalSidesLatex || "",
    },
  };
}

export function renderTask({ data }) {
  const { AB, ABLatex, BC, BCLatex, angleB, statements } = data;
  const renderStatement = (statement) => {
    if (statement.id === "isosceles") {
      return (
        <>
          Trójkąt <InlineMath math="ABC" /> jest równoramienny.
        </>
      );
    }

    return (
      <>
        Pole trójkąta <InlineMath math="ABC" /> jest równe{" "}
        <InlineMath math={statement.latex} />.
      </>
    );
  };

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne - zamknięte (0-1 pkt).</strong>
      </p>

      <p>
        Dany jest trójkąt <InlineMath math="ABC" />, w którym{" "}
        <InlineMath math={`|AB|=${ABLatex}`} />, <InlineMath math={`|BC|=${BCLatex}`} /> oraz{" "}
        <InlineMath math={`|\\angle ABC|=${angleB}^{\\circ}`} /> (zobacz rysunek).
      </p>

      <IncludedAngleTriangleDiagram AB={AB} ABLatex={ABLatex} BC={BC} BCLatex={BCLatex} angleB={angleB} />

      <p>
        Oceń prawdziwość poniższych stwierdzeń. Wybierz <InlineMath math="P" />,
        jeśli stwierdzenie jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>

      <p><strong>1.</strong> {renderStatement(statements[0])}</p>
      <p><strong>2.</strong> {renderStatement(statements[1])}</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, v) => onChange?.({ ...value, [key]: v });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput value={value.s1 || null} disabled={disabled} onChange={(v) => setValue("s1", v)} />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput value={value.s2 || null} disabled={disabled} onChange={(v) => setValue("s2", v)} />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  return (
    userAnswer.s1 === (data.statements[0].truth ? "P" : "F") &&
    userAnswer.s2 === (data.statements[1].truth ? "P" : "F")
  );
}

export function renderAnswer({ data }) {
  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>
        <strong>1.</strong> <InlineMath math={`\\text{${data.statements[0].truth ? "P" : "F"}}`} />
      </p>
      <p>
        <strong>2.</strong> <InlineMath math={`\\text{${data.statements[1].truth ? "P" : "F"}}`} />
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>
        1. <InlineMath math={`\\text{${userAnswer.s1 || "-"}}`} />
      </p>
      <p>
        2. <InlineMath math={`\\text{${userAnswer.s2 || "-"}}`} />
      </p>
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    AB,
    ABLatex,
    BC,
    BCLatex,
    angleB,
    isIsosceles,
    correctAreaLatex,
    displayedAreaLatex,
    areaTruth,
    acSquaredLatex,
    equalSidesLatex,
  } = data;
  const sinLatex = ANGLES[angleB].sin.latex;
  const cosLatex = ANGLES[angleB].cos.latex;
  const ABTerm = ABLatex.includes("\\") ? `\\left(${ABLatex}\\right)` : ABLatex;
  const BCTerm = BCLatex.includes("\\") ? `\\left(${BCLatex}\\right)` : BCLatex;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Sprawdzamy równoramienność. W tym zadaniu znamy boki{" "}
        <InlineMath math="AB" /> i <InlineMath math="BC" />.
      </p>

      <BlockMath math={`|AB|=${ABLatex},\\quad |BC|=${BCLatex}`} />

      {isIsosceles ? (
        <>
          <p>
            Podane boki nie muszą być równe. Liczymy trzeci bok z twierdzenia cosinusów:
          </p>
          <BlockMath
            math={`\\begin{aligned}|AC|^2&=${ABTerm}^2+${BCTerm}^2\\\\&\\quad-2\\cdot ${ABTerm}\\cdot ${BCTerm}\\cdot \\cos ${angleB}^{\\circ}\\end{aligned}`}
          />
          <BlockMath math={`|AC|^2=${acSquaredLatex}`} />
          <p>
            Otrzymujemy <InlineMath math={equalSidesLatex} />, więc trójkąt{" "}
            <InlineMath math="ABC" /> jest równoramienny. Pierwsze stwierdzenie jest{" "}
            <strong>prawdziwe</strong>.
          </p>
        </>
      ) : (
        <>
          <p>
            Boki <InlineMath math="AB" /> i <InlineMath math="BC" /> nie są równe. Dodatkowo z twierdzenia
            cosinusów możemy wyznaczyć trzeci bok:
          </p>
          <BlockMath
            math={`\\begin{aligned}|AC|^2&=${ABTerm}^2+${BCTerm}^2\\\\&\\quad-2\\cdot ${ABTerm}\\cdot ${BCTerm}\\cdot \\cos ${angleB}^{\\circ}\\end{aligned}`}
          />
          <BlockMath
            math={`\\begin{aligned}|AC|^2&=${ABTerm}^2+${BCTerm}^2\\\\&\\quad-2\\cdot ${ABTerm}\\cdot ${BCTerm}\\cdot ${cosLatex}\\end{aligned}`}
          />
          <BlockMath math={`|AC|^2=${acSquaredLatex}`} />
          <p>
            Ta długość nie daje pary równych boków, więc pierwsze stwierdzenie jest{" "}
            <strong>fałszywe</strong>.
          </p>
        </>
      )}

      <p>
        <strong>Krok 2.</strong> Pole liczymy ze wzoru na pole trójkąta z dwóch boków i kąta między nimi:
      </p>

      <BlockMath math={'P=\\frac{1}{2}ab\\sin\\gamma'} />
      <BlockMath math={`P=\\frac{1}{2}\\cdot ${ABTerm}\\cdot ${BCTerm}\\cdot \\sin ${angleB}^{\\circ}`} />
      <BlockMath math={`\\sin ${angleB}^{\\circ}=${sinLatex}`} />
      <BlockMath math={`P=\\frac{1}{2}\\cdot ${ABTerm}\\cdot ${BCTerm}\\cdot ${sinLatex}`} />
      <BlockMath math={`P=${correctAreaLatex}`} />

      <p>
        W drugim stwierdzeniu podano <InlineMath math={displayedAreaLatex} />, więc jest ono{" "}
        <strong>{areaTruth ? "prawdziwe" : "fałszywe"}</strong>.
      </p>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
